import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, type DocumentoVeiculo } from "../api";
import type { Row } from "../types";
import { IconClose, IconUpload, IconTrash, IconFile, IconEye, IconDownload } from "../icons";
import { usePodeExcluir } from "../permissoes";

/**
 * Documentos de um veículo (CRLV, seguro, IPVA...).
 *
 * Anexados aqui, aparecem para a equipe no Operacional, na aba "Documentos dos
 * Veículos" — os dois sistemas leem a mesma tabela (ver
 * server/veiculos-documentos.ts). Abrir/baixar usa os links assinados que a
 * lista devolve, então funciona também no celular.
 */

/** Mesma lista do servidor (TIPOS_DOCUMENTO). */
const TIPOS = ["CRLV", "Seguro", "IPVA", "Licenciamento", "Laudo / Vistoria", "Nota fiscal", "Manual", "Outro"];

/** AAAA-MM-DD → DD/MM/AAAA (sem passar por Date, para não escorregar de fuso). */
function dataBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : String(iso);
}

function hojeIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Vencido / vence em até 30 dias / válido. */
export function situacaoValidade(validade: string | null): { texto: string; tom: "danger" | "warn" | "neutral" } | null {
  if (!validade) return null;
  const hoje = hojeIso();
  if (validade < hoje) return { texto: `Vencido em ${dataBR(validade)}`, tom: "danger" };
  const dias = Math.round(
    (Date.parse(`${validade}T12:00:00Z`) - Date.parse(`${hoje}T12:00:00Z`)) / 86_400_000,
  );
  if (dias <= 30) return { texto: `Vence em ${dataBR(validade)}`, tom: "warn" };
  return { texto: `Válido até ${dataBR(validade)}`, tom: "neutral" };
}

function tamanhoTexto(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

interface Props {
  veiculo: Row;
  onClose: () => void;
}

export default function DocumentosVeiculoModal({ veiculo, onClose }: Props) {
  // Anexar segue a permissão de Veículos; apagar é só de administrador.
  const podeExcluir = usePodeExcluir();
  const [docs, setDocs] = useState<DocumentoVeiculo[]>([]);
  const [carregando, setCarregando] = useState(true);

  const [tipo, setTipo] = useState("CRLV");
  const [validade, setValidade] = useState("");
  const [descricao, setDescricao] = useState("");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const placa = String(veiculo.placa ?? "");
  const modelo = String(veiculo.modelo ?? "");

  const carregar = useCallback(async () => {
    try {
      setDocs(await api.documentosVeiculo.list(veiculo.id));
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível carregar os documentos.");
    } finally {
      setCarregando(false);
    }
  }, [veiculo.id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function anexar() {
    setErro(null);
    setAviso(null);
    if (!arquivo) {
      setErro("Escolha o arquivo do documento (PDF ou foto).");
      return;
    }
    setEnviando(true);
    try {
      await api.documentosVeiculo.anexar(veiculo.id, {
        file: arquivo,
        tipo,
        validade: validade || undefined,
        descricao: descricao.trim() || undefined,
      });
      setArquivo(null);
      setValidade("");
      setDescricao("");
      if (inputRef.current) inputRef.current.value = "";
      setAviso("Documento anexado. Já aparece para a equipe no Operacional.");
      await carregar();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível anexar o documento.");
    } finally {
      setEnviando(false);
    }
  }

  async function remover(doc: DocumentoVeiculo) {
    if (!confirm(`Remover o documento "${doc.tipo} — ${doc.nome_arquivo}"?\n\nEle some também do Operacional.`)) return;
    setErro(null);
    setAviso(null);
    try {
      await api.documentosVeiculo.remover(doc.id);
      await carregar();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível remover o documento.");
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div
        className="modal-card modal-lg"
        role="dialog"
        aria-modal="true"
        aria-label={`Documentos do veículo ${placa}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h3>
            Documentos — {placa}
            {modelo ? ` · ${modelo}` : ""}
          </h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Fechar" title="Fechar">
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <p className="docv-intro">
            Os documentos anexados aqui aparecem para a equipe no <strong>Operacional</strong>, na aba{" "}
            <strong>Documentos dos Veículos</strong>, junto com a placa, o modelo e os dados deste cadastro.
          </p>

          {/* Anexar — some para quem é somente visualização. */}
          <div className="cert-bloco modo-leitura-esconde">
            <h4 className="cert-titulo">Anexar documento</h4>
            <div className="cert-datas">
              <label className="field">
                <span>Tipo</span>
                <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
                  {TIPOS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Validade (opcional)</span>
                <input type="date" value={validade} onChange={(e) => setValidade(e.target.value)} />
              </label>
              <label className="field field-wide">
                <span>Descrição (opcional)</span>
                <input
                  type="text"
                  value={descricao}
                  maxLength={200}
                  placeholder="Ex.: CRLV 2026, apólice nº 123..."
                  onChange={(e) => setDescricao(e.target.value)}
                />
              </label>
            </div>
            <div className="cert-anexar">
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf,.pdf,image/jpeg,image/png,image/webp"
                style={{ display: "none" }}
                onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
              />
              <button
                type="button"
                className="btn-secondary"
                onClick={() => inputRef.current?.click()}
                disabled={enviando}
              >
                <IconFile size={16} /> {arquivo ? "Trocar arquivo" : "Escolher arquivo"}
              </button>
              {arquivo && (
                <span className="cert-arquivo">
                  {arquivo.name} · {tamanhoTexto(arquivo.size)}
                </span>
              )}
              <button type="button" className="btn-primary" onClick={anexar} disabled={enviando || !arquivo}>
                <IconUpload size={16} /> {enviando ? "Enviando…" : "Anexar"}
              </button>
            </div>
            <p className="hint">PDF ou foto (JPG, PNG), até 15 MB.</p>
          </div>

          <div className="cert-bloco">
            <h4 className="cert-titulo">Documentos anexados</h4>
            {carregando ? (
              <p className="muted">Carregando…</p>
            ) : docs.length === 0 ? (
              <p className="muted">Nenhum documento anexado ainda.</p>
            ) : (
              <table className="table table-compact docv-tabela">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Documento</th>
                    <th>Validade</th>
                    <th>Enviado</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => {
                    const sit = situacaoValidade(d.validade);
                    return (
                      <tr key={d.id}>
                        <td>
                          <strong>{d.tipo}</strong>
                        </td>
                        <td className="docv-nome">
                          {d.nome_arquivo}
                          <span className="muted"> · {tamanhoTexto(d.tamanho)}</span>
                          {d.descricao ? <div className="muted">{d.descricao}</div> : null}
                        </td>
                        <td>
                          {sit ? (
                            sit.tom === "neutral" ? (
                              <span className="muted">{sit.texto}</span>
                            ) : (
                              <span className={`pill pill-${sit.tom}`}>{sit.texto}</span>
                            )
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          {dataBR(String(d.criado_em).slice(0, 10))}
                          {d.enviado_por ? <div className="muted">{d.enviado_por}</div> : null}
                        </td>
                        <td className="col-actions">
                          <a className="icon-btn" href={d.url} target="_blank" rel="noopener" title="Abrir">
                            <IconEye size={16} />
                          </a>
                          <a className="icon-btn" href={d.url_baixar} title="Baixar">
                            <IconDownload size={16} />
                          </a>
                          {podeExcluir && (
                            <button
                              type="button"
                              className="icon-btn icon-btn-danger"
                              title="Remover documento"
                              onClick={() => void remover(d)}
                            >
                              <IconTrash size={16} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {aviso && <div className="form-ok">{aviso}</div>}
          {erro && <div className="form-error">{erro}</div>}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
